import { NPCS } from '../../content/npcs';
import type { Rng } from '../../core/rng';
import { CARDS } from '../../game/build';
import type { CharacterSave } from '../../game/character';
import { countOf, removeStack } from '../../game/inventory';
import { ITEMS } from '../../game/items';
import { BOND_STAGES, BOND_STAGE_NAMES, type Progression } from '../../game/Progression';
import {
  EMOTES_PER_DAY,
  EMOTE_BY_ID,
  GIFTS_PER_DAY,
  GIFT_MULT,
  type GiftPref,
  SONGS_PER_DAY,
  SONG_BY_ID,
  profileFor,
} from './data';

/**
 * Bond rules. Points are per character (`char.bonds`, via Progression.addBond);
 * daily counters and claimed stage rewards live in `char.flags` under
 * `bonds.*` and reset when `account.day` changes.
 */

const F = { day: 'bonds.day', songs: 'bonds.songs', emotes: 'bonds.emotes', gifts: 'bonds.gifts' } as const;
const sungKey = (npc: string) => `bonds.sung.${npc}`;
const rewardKey = (npc: string) => `bonds.reward.${npc}`;
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

export const MAX_STAGE = BOND_STAGES.length - 1;

// ================================================================ daily counters

export interface BondDaily {
  songs: number;
  emotes: number;
  gifts: number;
}

/** Resets the daily counters when the session day changed. */
export function syncDay(c: CharacterSave, day: number): void {
  if (c.flags[F.day] === day) return;
  c.flags[F.day] = day;
  c.flags[F.songs] = 0;
  c.flags[F.emotes] = 0;
  c.flags[F.gifts] = 0;
  for (const k of Object.keys(c.flags)) if (k.startsWith('bonds.sung.')) delete c.flags[k];
}

export function dailyUsed(c: CharacterSave, day: number): BondDaily {
  syncDay(c, day);
  return { songs: num(c.flags[F.songs]), emotes: num(c.flags[F.emotes]), gifts: num(c.flags[F.gifts]) };
}

/** Songs already played for this NPC today. */
export function songsHeardToday(c: CharacterSave, day: number, npc: string): string[] {
  syncDay(c, day);
  const v = c.flags[sungKey(npc)];
  return typeof v === 'string' && v ? v.split(',') : [];
}

// ================================================================ points

export function songPoints(npc: string, song: string, rng: Rng): number {
  const fav = profileFor(npc).favoriteSong === song;
  return Math.round(rng.int(20, 40) * (fav ? 1.5 : 1));
}

export function emotePoints(npc: string, emote: string, rng: Rng): number {
  const p = profileFor(npc);
  const k = p.likedEmote === emote ? 1.5 : p.dislikedEmote === emote ? 0.5 : 1;
  return Math.round(rng.int(10, 20) * k);
}

export function giftPreference(npc: string, itemId: string): GiftPref {
  const p = profileFor(npc);
  if (p.loves.includes(itemId)) return 'loved';
  if (p.likes.includes(itemId)) return 'liked';
  if (p.dislikes.includes(itemId)) return 'disliked';
  return 'neutral';
}

/** Stackable items an NPC will accept: materials, consumables and battle items with a sale value. */
export function isGiftable(itemId: string): boolean {
  const d = ITEMS[itemId];
  return !!d && d.value > 0 && (d.kind === 'material' || d.kind === 'consumable' || d.kind === 'battle' || d.kind === 'food');
}

/** Points for one gift: scales with the item's value, then by the NPC's taste. */
export function giftPoints(npc: string, itemId: string): number {
  const d = ITEMS[itemId];
  if (!d) return 0;
  const base = Math.max(5, Math.min(80, Math.round(6 + d.value * 0.9)));
  return Math.max(1, Math.round(base * GIFT_MULT[giftPreference(npc, itemId)]));
}

// ================================================================ stage rewards

export interface StageReward {
  stage: number;
  silver: number;
  items: { id: string; count: number }[];
  card: string | null;
  title: string | null;
}

/** What the Bonds feature adds at each stage (on top of Progression's built-in silver/skill potion). */
export function stageExtras(npc: string, stage: number): StageReward {
  const p = profileFor(npc);
  const out: StageReward = { stage, silver: 0, items: [], card: null, title: null };
  if (stage === 1) out.items.push({ id: 'potion_small', count: 3 });
  if (stage === 2) {
    if (p.card && CARDS[p.card]) out.card = p.card;
    else out.items.push({ id: 'potion_major', count: 2 });
  }
  if (stage === 3) out.items.push(...p.trusted.map((t) => ({ ...t })));
  if (stage === 4) out.title = p.title;
  return out;
}

/** Full reward preview for a stage, including Progression.addBond's built-in rewards. */
export function stageRewards(npc: string, stage: number): StageReward {
  const r = stageExtras(npc, stage);
  if (stage <= 0) return r;
  r.silver += 500 * stage;
  if (stage >= 2) r.items.unshift({ id: 'skill_potion', count: 1 });
  return r;
}

export function claimedStage(c: CharacterSave, npc: string): number {
  return num(c.flags[rewardKey(npc)]);
}

/** Grants the Bonds extras for every stage reached but not yet rewarded. */
export function claimStageRewards(prog: Progression, npc: string): StageReward[] {
  const c = prog.char;
  const stage = prog.bondStage(npc);
  let claimed = claimedStage(c, npc);
  const out: StageReward[] = [];
  while (claimed < stage) {
    claimed++;
    const r = stageExtras(npc, claimed);
    if (r.items.length || r.card) prog.grantDrop({ items: r.items, equip: [], silver: 0, gold: 0, cards: r.card ? [r.card] : [] }, `Bond: ${NPCS[npc]?.name ?? npc}`);
    if (r.title && !c.titles.includes(r.title)) {
      c.titles.push(r.title);
      prog.emit('toast', { text: `Title earned: ${r.title}`, color: '#ff90c0' });
    }
    out.push(r);
  }
  c.flags[rewardKey(npc)] = claimed;
  return out;
}

// ================================================================ actions

export type BondResult =
  | { ok: true; points: number; line: string; stage: number; stageUp: boolean; rewards: StageReward[]; tone: 'great' | 'good' | 'poor' }
  | { ok: false; reason: string };

function validate(prog: Progression, npc: string): string | null {
  if (!NPCS[npc]?.bond) return 'You cannot bond with them.';
  if (prog.bondStage(npc) >= MAX_STAGE) return `Already ${BOND_STAGE_NAMES[MAX_STAGE] ?? 'Devoted'}`;
  return null;
}

function apply(prog: Progression, npc: string, points: number, line: string, tone: 'great' | 'good' | 'poor'): BondResult {
  const before = prog.bondStage(npc);
  prog.addBond(npc, points);
  const rewards = claimStageRewards(prog, npc);
  const stage = prog.bondStage(npc);
  return { ok: true, points, line, stage, stageUp: stage > before, rewards, tone };
}

export function canPlaySong(c: CharacterSave, day: number, npc: string, song: string): string | null {
  if (!SONG_BY_ID[song]) return 'Unknown song';
  if (dailyUsed(c, day).songs >= SONGS_PER_DAY) return 'No songs left today';
  if (songsHeardToday(c, day, npc).includes(song)) return 'They heard that one today';
  return null;
}

/** Play a song for an NPC: 5 per day, each song once per NPC per day, +20–40 (favourite ×1.5). */
export function playSong(prog: Progression, day: number, npc: string, song: string, rng: Rng = prog.rng): BondResult {
  const bad = validate(prog, npc) ?? canPlaySong(prog.char, day, npc, song);
  if (bad) return { ok: false, reason: bad };
  const c = prog.char;
  c.flags[F.songs] = num(c.flags[F.songs]) + 1;
  c.flags[sungKey(npc)] = [...songsHeardToday(c, day, npc), song].join(',');
  const p = profileFor(npc);
  const fav = p.favoriteSong === song;
  return apply(prog, npc, songPoints(npc, song, rng), fav ? p.lines.favorite : p.lines.song, fav ? 'great' : 'good');
}

/** Perform an emote for an NPC: 5 per day, +10–20 (liked ×1.5, disliked ×0.5). */
export function emoteFor(prog: Progression, day: number, npc: string, emote: string, rng: Rng = prog.rng): BondResult {
  const bad = validate(prog, npc) ?? (!EMOTE_BY_ID[emote] ? 'Unknown emote' : dailyUsed(prog.char, day).emotes >= EMOTES_PER_DAY ? 'No emotes left today' : null);
  if (bad) return { ok: false, reason: bad };
  const c = prog.char;
  c.flags[F.emotes] = num(c.flags[F.emotes]) + 1;
  const p = profileFor(npc);
  const liked = p.likedEmote === emote;
  const disliked = p.dislikedEmote === emote;
  return apply(prog, npc, emotePoints(npc, emote, rng), liked ? p.lines.liked : disliked ? p.lines.disliked : p.lines.emote, liked ? 'great' : disliked ? 'poor' : 'good');
}

/** Give one item from the bag: 20 per day; points from the item's value and the NPC's taste. */
export function giveGift(prog: Progression, day: number, npc: string, itemId: string): BondResult {
  const c = prog.char;
  const bad =
    validate(prog, npc) ??
    (!isGiftable(itemId) ? 'They would not want that.' : countOf(c, itemId) <= 0 ? 'You have none left.' : dailyUsed(c, day).gifts >= GIFTS_PER_DAY ? 'No gifts left today' : null);
  if (bad) return { ok: false, reason: bad };
  if (!removeStack(c, itemId, 1)) return { ok: false, reason: 'You have none left.' };
  c.flags[F.gifts] = num(c.flags[F.gifts]) + 1;
  const p = profileFor(npc);
  const pref = giftPreference(npc, itemId);
  const line = pref === 'loved' ? p.lines.loved : pref === 'disliked' ? p.lines.disdain : p.lines.gift;
  return apply(prog, npc, giftPoints(npc, itemId), line, pref === 'loved' ? 'great' : pref === 'disliked' ? 'poor' : 'good');
}

/** Points toward the next stage: { into, need } (need 0 at the top stage). */
export function stageProgress(points: number): { stage: number; into: number; need: number } {
  let stage = 0;
  BOND_STAGES.forEach((t, i) => {
    if (points >= t) stage = i;
  });
  const lo = BOND_STAGES[stage] ?? 0;
  const hi = BOND_STAGES[stage + 1];
  return hi === undefined ? { stage, into: 0, need: 0 } : { stage, into: points - lo, need: hi - lo };
}
